import { schemaText } from './text'
import { generateFAQPage } from './faqPage'

type Node = Record<string, any>
export type PageKind = 'page' | 'blog' | 'article' | 'collection' | 'author' | 'resource' | 'event'
export interface PageGraphInput {
  path: string
  siteHandle: string
  entry: Node | null | undefined
  metaTags?: Array<{ name?: string; property?: string; content?: string }>
  kind?: PageKind
  byline?: boolean
  videos?: Array<Node | null>
  /** Enabled public ancestor entries queried for this site, without preview access. */
  breadcrumbEntries?: Array<{ url?: string | null }>
}

// Entity IDs describe the public site even when rendered in a preview.
const ORIGIN = 'https://delight.ai'
const IDENTITY = `${ORIGIN}/#identity`
const WEBSITE = `${ORIGIN}/#website`
const PRODUCT = `${ORIGIN}/#product`
const ref = (id: string) => ({ '@id': id })

function validDate(value: unknown): string | undefined {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value) && Number.isFinite(Date.parse(value)) ? value : undefined
}

function locale(path: string, siteHandle: string) {
  const prefix = path.match(/^\/(ko|ja)(?=\/|$)/)?.[0]
    || (siteHandle === 'delightAi_ko_KR' ? '/ko' : siteHandle === 'delightAi_ja_JP' ? '/ja' : '')
  return { prefix, language: prefix === '/ko' ? 'ko-KR' : prefix === '/ja' ? 'ja-JP' : 'en-US' }
}

function pagePath(path: string, prefix: string): string {
  const clean = `/${path.split(/[?#]/)[0] || ''}`.replace(/\/{2,}/g, '/').replace(/\/+$/, '')
  return `${prefix}${clean.replace(/^\/(ko|ja)(?=\/|$)/, '')}` || '/'
}

function person(entry: Node, url: string): Node | undefined {
  const name = schemaText(entry['authorName'])
  if (!name) return
  const image = entry['image']?.[0]?.url
  return { '@type': 'Person', '@id': `${url}#person`, name, url, ...(image && { image }) }
}

function authorPerson(entry: Node): Node | undefined {
  if (typeof entry['url'] !== 'string') return
  try {
    const target = new URL(entry['url'], ORIGIN)
    if (target.origin !== ORIGIN || !/^\/(?:ko\/|ja\/)?author\/[^/]+\/?$/.test(target.pathname)) return
    return person(entry, `${ORIGIN}${target.pathname.replace(/\/+$/, '')}`)
  } catch { return }
}

/** Build the graph from the entry and metadata that actually render the page. */
export function buildPageGraph(input: PageGraphInput): Node | null {
  const { entry, metaTags = [], kind = 'page' } = input
  if (!entry || entry['id'] === 'not-found') return null
  const { prefix, language } = locale(input.path, input.siteHandle)
  const path = pagePath(input.path, prefix)
  const relative = path.replace(/^\/(ko|ja)(?=\/|$)/, '') || '/'
  const url = `${ORIGIN}${path}`
  const home = `${ORIGIN}${prefix || '/'}`
  const enabledAncestorUrls = new Set((input.breadcrumbEntries || []).flatMap(entry => {
    if (!entry.url) return []
    try {
      const target = new URL(entry.url)
      // Never cross languages/domains or turn a preview URL into an ancestor.
      if (target.origin !== ORIGIN || target.search || target.hash) return []
      if (prefix && !target.pathname.startsWith(`${prefix}/`) && target.pathname !== prefix) return []
      return [`${ORIGIN}${target.pathname.replace(/\/+$/, '') || '/'}`]
    } catch { return [] }
  }))
  const canLinkAncestor = (target: string) => !prefix || enabledAncestorUrls.has(target)
  const meta = (key: string) => schemaText(metaTags.find(tag => tag.name === key || tag.property === key)?.content)
  const title = meta('og:title') || schemaText(entry['authorName'] || entry['title'])
  if (!title) return null
  const description = meta('description')
  const datePublished = validDate(entry['postDate'])
  const dateUpdated = validDate(entry['dateUpdated'])
  // Scheduled/imported entries can have an edit timestamp before publication.
  // Omit that invalid public sequence until the CMS dates are corrected; do not
  // invent a later edit or change the publication date to hide the discrepancy.
  const dateModified = dateUpdated && (!datePublished || Date.parse(dateUpdated) >= Date.parse(datePublished))
    ? dateUpdated : undefined
  const dates = {
    ...(datePublished && { datePublished }),
    ...(dateModified && { dateModified }),
  }
  const graph: Node[] = [
    {
      '@type': 'Organization', '@id': IDENTITY, name: 'Delight.ai', url: `${ORIGIN}/`,
      description: 'Delight.ai is an AI customer agent for revenue generation.',
      logo: { '@type': 'ImageObject', url: `${ORIGIN}/logo.svg` },
      sameAs: ['https://www.linkedin.com/company/get-delight/', 'https://x.com/GetDelightAI', 'https://www.youtube.com/channel/UCU8BBGoVnbKcDA265ng9nKw'],
      address: [
        { '@type': 'PostalAddress', streetAddress: '100 S. Ellsworth Ave, Suite 900', addressLocality: 'San Mateo', addressRegion: 'CA', postalCode: '94401', addressCountry: 'US' },
        { '@type': 'PostalAddress', streetAddress: '17F, 514, Seolleung-ro', addressLocality: 'Gangnam-gu', addressRegion: 'Seoul', postalCode: '06162', addressCountry: 'KR' },
        { '@type': 'PostalAddress', streetAddress: 'Level 17, Frasers Tower, 182 Cecil Street', addressLocality: 'Singapore', postalCode: '069547', addressCountry: 'SG' },
      ],
      contactPoint: [{ '@type': 'ContactPoint', contactType: 'sales', url: `${ORIGIN}/contact-sales`, availableLanguage: ['English', 'Korean'] }],
    },
    { '@type': 'WebSite', '@id': WEBSITE, name: 'Delight.ai', url: `${ORIGIN}/`, publisher: ref(IDENTITY), inLanguage: ['en-US', 'ko-KR', 'ja-JP'] },
  ]
  const page: Node = {
    '@type': kind === 'author' ? 'ProfilePage' : kind === 'collection' ? 'CollectionPage'
      : kind === 'page' && ['WebPage', 'ContactPage'].includes(entry['schemaPageType']) ? entry['schemaPageType']
      : ['/contact-sales', '/demo-request', '/homeservice-demo-request', '/steward-demo-request', '/zendesk-demo'].includes(relative) ? 'ContactPage' : 'WebPage',
    '@id': `${url}#webpage`, url, name: title, ...(description && { description }),
    inLanguage: language, isPartOf: ref(WEBSITE), publisher: ref(IDENTITY), ...dates,
  }
  graph.push(page)

  const imageUrl = metaTags.find(tag => tag.property === 'og:image')?.content || entry['image']?.[0]?.url
  if (typeof imageUrl === 'string' && /^https?:\/\//.test(imageUrl)) {
    graph.push({ '@type': 'ImageObject', '@id': `${url}#primaryimage`, url: imageUrl, contentUrl: imageUrl, caption: title })
    page['primaryImageOfPage'] = ref(`${url}#primaryimage`)
  }

  // Do not invent /author, /industries, /tools, /compare or /events hubs.
  const homeNames: Record<string, string> = { 'en-US': 'Home', 'ko-KR': '홈', 'ja-JP': 'ホーム' }
  const items: Node[] = canLinkAncestor(home)
    ? [{ '@type': 'ListItem', position: 1, name: homeNames[language], item: home }] : []
  const hubs: Record<string, string[]> = {
    blog: ['Blog', '블로그', 'ブログ'], perspective: ['Perspectives', '인사이트', 'インサイト'],
    customers: ['Customers', '고객 사례', '導入事例'], 'product-release': ['Product releases', '제품 업데이트', '製品アップデート'],
    glossary: ['Glossary', '용어집', '用語集'], resources: ['Resources', '리소스', 'リソース'],
    ebooks: ['eBooks', '전자책', '電子書籍'], podcasts: ['Podcasts', '팟캐스트', 'ポッドキャスト'],
    webinars: ['Webinars', '웨비나', 'ウェビナー'],
  }
  const root = relative.split('/')[1] || ''
  const hub = hubs[root]
  if (relative.split('/').filter(Boolean).length > 1 && hub && canLinkAncestor(`${ORIGIN}${prefix}/${root}`)) {
    items.push({ '@type': 'ListItem', position: items.length + 1, name: hub[prefix === '/ko' ? 1 : prefix === '/ja' ? 2 : 0], item: `${ORIGIN}${prefix}/${root}` })
  }
  const category = entry['blogCategories']?.[0]
  if (kind === 'blog' && category?.slug && category?.title && relative.startsWith(`/blog/${category.slug}/`)
    && canLinkAncestor(`${ORIGIN}${prefix}/blog/${category.slug}`)) {
    items.push({ '@type': 'ListItem', position: items.length + 1, name: schemaText(category.title), item: `${ORIGIN}${prefix}/blog/${category.slug}` })
  }
  if (relative !== '/') {
    // Generic entries often have internal admin titles (e.g. "Contact Sales KO").
    // Their public metadata supplies the localized label; editorial entries and
    // author profiles retain the title/name displayed in their page header.
    const breadcrumbName = kind === 'page' || kind === 'collection'
      ? title : schemaText(entry['authorName'] || entry['title']) || title
    items.push({ '@type': 'ListItem', position: items.length + 1, name: breadcrumbName, item: url })
    if (items.length > 1) {
      graph.push({ '@type': 'BreadcrumbList', '@id': `${url}#breadcrumb`, itemListElement: items })
      page['breadcrumb'] = ref(`${url}#breadcrumb`)
    }
  }

  if (kind === 'blog' || kind === 'article') {
    // Only the first author is rendered by the blog/glossary/perspective headers. A CMS
    // relation on a customer story does not establish a visible byline.
    const authors = kind === 'blog' || input.byline
      ? (entry['authorEntry'] || []).slice(0, 1).map((author: Node) => authorPerson(author)).filter(Boolean) as Node[]
      : []
    graph.push(...authors)
    const article: Node = {
      '@type': kind === 'blog' ? 'BlogPosting' : 'Article', '@id': `${url}#article`,
      headline: schemaText(entry['title']) || title, url, ...(description && { description }), ...dates,
      inLanguage: language, author: authors.length ? authors.map(author => ref(author['@id'])) : ref(IDENTITY),
      publisher: ref(IDENTITY), mainEntityOfPage: ref(`${url}#webpage`),
      ...(page['primaryImageOfPage'] && { image: page['primaryImageOfPage'] }),
      ...(category?.title && { articleSection: schemaText(category.title) }),
    }
    graph.push(article)
    page['mainEntity'] = ref(article['@id'])
  }

  if (kind === 'author') {
    const author = person(entry, url)
    if (author) {
      const jobTitle = schemaText(entry['jobTitle'])
      const bio = schemaText(entry['bio'])
      if (jobTitle) author['jobTitle'] = jobTitle
      if (bio) author['description'] = bio
      graph.push(author)
      page['mainEntity'] = ref(author['@id'])
    }
  }

  // Prices, ratings, feature lists and corporate relationships have no safe default.
  if (kind === 'page' && ['/ai-agent', '/desk'].includes(relative)) {
    const productId = relative === '/desk' ? `${ORIGIN}/desk#product` : PRODUCT
    graph.push({
      '@type': 'SoftwareApplication', '@id': productId, name: relative === '/desk' ? 'Delight Desk' : 'Delight.ai',
      url, ...(description && { description }), applicationCategory: 'BusinessApplication', operatingSystem: 'Web', publisher: ref(IDENTITY),
      ...(page['primaryImageOfPage'] && { image: page['primaryImageOfPage'] }),
    })
    page['mainEntity'] = ref(productId)
  } else if (kind === 'page' && relative === '/') {
    page['about'] = ref(IDENTITY)
  }

  const blocks = [...(entry['hero'] || []), ...(entry['pageBuilder'] || []), ...(entry['blogPageBuilder'] || [])]
  const faq = generateFAQPage(blocks)
  if (faq) {
    const { '@context': _, ...node } = faq
    graph.push({ ...node, '@id': `${url}#faq`, isPartOf: ref(`${url}#webpage`), inLanguage: language })
  }

  const seenVideos = new Set<string>()
  for (const video of input.videos || []) {
    // Google requires the video's actual first-publication date. The page's
    // postDate/dateUpdated (or a CMS asset import date) is not a substitute.
    // Keep playback independent: omit incomplete markup until the video source
    // supplies a verified date instead of publishing an invalid VideoObject.
    const uploadDate = validDate(video?.['uploadDate'])
    if (!video?.['embedUrl'] || !uploadDate || seenVideos.has(video['embedUrl'])) continue
    seenVideos.add(video['embedUrl'])
    const { '@context': _, ...node } = video
    graph.push({ ...node, uploadDate, '@id': `${url}#video-${seenVideos.size}`, isPartOf: ref(`${url}#webpage`), publisher: ref(IDENTITY) })
  }
  return { '@context': 'https://schema.org', '@graph': [...new Map(graph.map(node => [node['@id'], node])).values()] }
}
